import { STORY_SOURCE, story } from '@/content/storyBuilder'

// The fifth written page. Everything below comes from the three-part full-page
// capture of digitopia.co/customers/proven/ sent on 2026-08-28 — "birebir tıpa
// tıp aynısını tasarla" — and the one photograph supplied with it (the DMI
// recommendations on a boardroom screen), which the reference runs beside "The
// Solution". The closing quote is not a `section`: Johann Heaven's words are
// already in `content/testimonials.ts` keyed to this slug, portrait and mark
// included, and `StoryBody` draws that card — which is the "testimonial
// template" the same request asked for at the foot of the page.
export const added = story('proven', 'PROVEN', {
  doc: '3a1c7f70-0ad8-43a1-9edb-2d2e1a7fe2a2-Proven and Digitopia.pdf',
  // The reference's own H1, taken up on 2026-08-29 when the same page was
  // asked for again as an exact copy — the previous check offered it and this
  // is the word. Every other story still heads on the company's name.
  headline: 'PROVEN’s Digital Transformation Success Story',
  // The header picture the reference runs beside the headline, supplied on
  // 2026-08-29 — so the header shows it rather than PROVEN's mark, exactly as
  // digitopia.co/customers/proven/ does. The file is 768x512, which is the
  // width the live site serves; a larger original would sharpen it.
  heroPhoto: {
    src: '/photo/businessman-paying-by-card-at-cafe.webp',
    alt: 'A smiling businessman at a café table holding a bank card in one hand and using a '
      + 'tablet with the other.',
  },
  lede: [
    'See how PROVEN enhanced their digital maturity and gained a competitive edge with '
    + 'Digital Maturity Index. Discover the power of data-driven insights and actionable '
    + 'roadmaps for growth.',
  ],
  sections: [
    {
      heading: 'The Challenge: Navigating Complexity Across Multiple Industries and Geographies',
      band: true,
      centred: true,
      body: [
        'PROVEN, a leading Caribbean group with a diverse portfolio spanning banking, wealth '
        + 'management, private capital, and real estate, faced increasing competition across '
        + 'multiple markets. As they sought to adapt and innovate in a complex, ever-evolving '
        + 'landscape, PROVEN’s leadership recognized the need to benchmark and enhance their '
        + 'digital capabilities. They needed a unified framework to assess their digital '
        + 'maturity across various business units and geographical locations to identify gaps '
        + 'and strategic opportunities for growth.',
      ],
    },
    {
      heading: 'The Solution: The Digital Maturity Index (DMI)',
      band: true,
      photo: {
        src: '/photo/boardroom-daimi-presentation.webp',
        alt: 'A woman presenting the Impact Platform’s DMI recommendations and priorities '
          + 'chart on a large screen to colleagues around a boardroom table.',
      },
      photoSide: 'right',
      photoInPanel: true,
      body: [
        'To overcome their challenges, PROVEN partnered with Digitopia to implement the '
        + 'Digital Maturity Index (DMI) across six key business units. The DMI provided:',
      ],
      items: [
        {
          lead: 'Holistic Assessment',
          text: 'A comprehensive view of PROVEN’s digital maturity across banking, wealth '
            + 'management, insurance, private capital, and real estate.',
        },
        {
          lead: 'Competitive Benchmarking',
          text: 'Clear insights into how PROVEN compares to industry peers, focusing efforts '
            + 'on high-impact areas.',
        },
        {
          lead: 'Interactive Workshops',
          text: 'Six engaging sessions offering strategic and practical insights, creating an '
            + 'internal library of best practices.',
        },
        {
          lead: 'Actionable Roadmap',
          text: 'A tailored playbook to accelerate digital maturity, enhance services, and '
            + 'boost competitiveness.',
        },
      ],
    },
    {
      heading: 'The Results: Empowering PROVEN for Future Success',
      band: true,
      // Numbered on the reference, and numbered here: the six are the order the
      // story tells them in, not a set of four bullets like the Solution's.
      ordered: true,
      body: [
        'The results of the collaboration were transformative. Here’s how PROVEN benefited '
        + 'from the DMI solution:',
      ],
      items: [
        {
          lead: 'Clear Digital Maturity Awareness',
          text: 'Leadership gained precise insights into digital maturity gaps, validating '
            + 'assumptions and guiding improvements.',
        },
        {
          lead: 'Cross-Business Unit Alignment',
          text: 'Workshops fostered collaboration, aligning all units under a unified digital '
            + 'transformation vision.',
        },
        {
          lead: 'Renewed Urgency',
          text: 'Metrics and benchmarks sparked faster, more informed decision-making.',
        },
        {
          lead: 'Prioritized Investments',
          text: 'Data-backed recommendations enabled smarter investments for maximum impact.',
        },
        {
          lead: 'Future-Ready Culture',
          text: 'The DMI exercise helped cultivate a culture of continuous innovation and '
            + 'adaptability.',
        },
        {
          lead: 'Strong Leadership Support',
          text: 'Full backing from Mr. Johann Heaven and the board ensures cohesive action '
            + 'across the organization.',
        },
      ],
    },
  ],
  check:
    'This page’s words were transcribed from a three-part full-page capture of '
    + `${STORY_SOURCE}/proven/ supplied on 2026-08-28, not from a file we hold, so please `
    + 'proofread it against the source before production — in particular “six key business '
    + 'units” against the five named under “The Solution” (banking, wealth management, '
    + 'insurance, private capital, real estate), which is the reference’s own arithmetic and '
    + 'not a transcription slip. Two deliberate differences from the reference are left, both '
    + 'of them a template-wide decision rather than this page’s: there is no “SUCCESS STORIES” '
    + 'eyebrow above the headline, because those blue eyebrows were taken off every customer '
    + 'page on request; and the reference’s “Read more” button inside the Results panel '
    + 'is here the one “Read the full story (PDF)” button in the header, pointing at the same '
    + 'document. The header photograph the reference runs — a man paying by card at a café '
    + 'table — arrived on 2026-08-29 and is now in that slot, at the 768x512 the live site '
    + 'serves; it will look soft on a high-density screen until a larger original arrives.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 5
